import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import App from './App'
import { ThemeProvider } from './contexts/ThemeContext'
import { useThemeContext } from './contexts/ThemeContext'
import { LoadingFallback } from '@/design-system/atoms/LoadingFallback'
import { getBrowserAdapter } from './lib/browserAdapter'
import { ENV } from './config/env'
import { warmPublicRouteChunk } from './app/routes'
import './i18n'

const browserAdapter = getBrowserAdapter()

// Kick off the current public route's lazy chunk download now, in parallel
// with the initial render, instead of waiting for React to mount the route
// and discover the import (TRA-115).
void warmPublicRouteChunk(browserAdapter.getLocation().pathname)

// Sentry loads after the window 'load' event so its bundle stays off the
// critical path of the public routes (TRA-115), and only when explicitly
// enabled via VITE_SENTRY_ENABLED=true with a DSN configured (TRA-114).
// Disabled by default keeps local dev and CI silent; without the flag the
// bundle is never even downloaded. Errors thrown before init are not
// captured — an accepted tradeoff while pre-launch.
if (ENV.SENTRY_ENABLED === 'true' && ENV.SENTRY_DSN) {
    const initializeSentryDeferred = () => {
        void import('./lib/sentry').then((m) => m.initializeSentry())
    }
    if (browserAdapter.document?.readyState === 'complete') {
        initializeSentryDeferred()
    } else {
        browserAdapter.addEventListener('load', initializeSentryDeferred)
    }
}

/**
 * Wrapper component that applies MUI theme from ThemeContext.
 * This component must be inside ThemeProvider.
 */
export function AppWithMuiTheme() {
    const { theme } = useThemeContext()

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <Suspense fallback={<LoadingFallback />}>
                <App />
            </Suspense>
        </MuiThemeProvider>
    )
}

const rootElement = browserAdapter.getElementById('root')
if (!rootElement) {
    throw new Error('Root element not found')
}
ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
        <BrowserRouter>
            <ThemeProvider>
                <AppWithMuiTheme />
            </ThemeProvider>
        </BrowserRouter>
    </React.StrictMode>
)
